import { OrganizationType } from '@graphql/enums';
import { useAuthContext } from '@shared/auth';
import { useMe } from '@store/entities/me/hooks';
import { useMeOrgSelector } from '@store/entities/meOrg/hooks';

/**
 * Hook to get the current user's organization type
 * Uses useMeOrgSelector to fetch organization data if not already in store
 * @returns OrganizationType (MERCHANT, BANK, or PLATFORM) or null if not available
 */
export const useOrganizationType = () => {
  const { authenticated, authenticating } = useAuthContext();
  const { me } = useMe();
  const organizationId = me?.organizationId ?? '';
  const hasOrganizationId = organizationId !== '';

  // Use useMeOrgSelector to automatically fetch organization if we have organizationId
  // The hook will only fetch if organization is not already in store
  const { org, loading } = useMeOrgSelector({
    organizationId: organizationId,
  });

  // Before `me` hydrates, an authenticated user's organizationId is simply not
  // known yet — that is still loading, not "no organization". Only a hydrated
  // `me` without organizationId means the user genuinely has no org.
  const isLoading = !me
    ? authenticating || authenticated
    : hasOrganizationId
      ? loading || !org?.type
      : false;

  return {
    orgType: (org?.type as OrganizationType) ?? null,
    loading: isLoading,
  };
};

/**
 * Check if the current user's organization is a merchant
 */
export const useIsMerchant = () => {
  const { orgType } = useOrganizationType();

  return orgType === OrganizationType.Merchant;
};

/**
 * Check if the current user's organization is a platform
 */
export const useIsPlatform = () => {
  const { orgType } = useOrganizationType();

  return orgType === OrganizationType.Platform;
};

/**
 * Check if the current user's organization is a bank
 * Returns { isBank, loading } to handle loading state properly
 */
export const useIsBank = () => {
  const { orgType, loading } = useOrganizationType();

  return {
    isBank: orgType === OrganizationType.Bank,
    isBankLoading: loading,
  };
};

/**
 * @deprecated Use useIsPlatform() || useIsBank() instead
 * Check if the current user's organization is platform or bank
 */
export const useIsPlatformOrBank = () => {
  const isPlatform = useIsPlatform();
  const { isBank } = useIsBank();

  return isPlatform || isBank;
};
